import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Download, User, Calendar, Package, Palette, Settings, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { brandSpecsManager, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const ViewBrandSpecs = () => {
  const { id } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const currentUser = authManager.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    if (id) {
      const brandData = brandSpecsManager.getBrandById(parseInt(id));
      if (brandData) {
        setBrand(brandData);
      } else {
        toast({
          title: 'Brand Not Found',
          description: 'The requested brand specification could not be found.',
          variant: 'destructive'
        });
      }
      setLoading(false);
    }
  }, [id, toast]);

  const handleDownload = () => {
    if (brand) {
      const result = brandSpecsManager.downloadBrandById(brand.id);
      if (result.success) {
        toast({
          title: 'Download Started',
          description: result.message,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading brand specifications...</p>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Brand Not Found</h1>
          <p className="text-gray-600 mb-6">The requested brand specification could not be found.</p>
          <Link to="/brand-specs">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Brand Specs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/brand-specs">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Brand Specs
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{brand.brandName}</h1>
              <p className="text-gray-600">{brand.buyerCompany} • {brand.brandCode}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Link to={`/brand-specs/edit/${brand.id}`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Edit Specs
              </Button>
            </Link>
            {isAdmin && (
              <Button variant="outline" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Specifications */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Client Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Brand Information</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Brand Name:</strong> {brand.brandName}</p>
                      <p><strong>Brand Code:</strong> {brand.brandCode}</p>
                      <p><strong>Company:</strong> {brand.buyerCompany}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Contact Information</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      {brand.contactPerson && <p><strong>Contact:</strong> {brand.contactPerson}</p>}
                      {brand.clientPhone && <p><strong>Phone:</strong> {brand.clientPhone}</p>}
                      {brand.clientEmail && <p><strong>Email:</strong> {brand.clientEmail}</p>}
                    </div>
                  </div>
                </div>
                {brand.clientAddress && (
                  <div>
                    <h4 className="font-medium text-gray-900">Address</h4>
                    <p className="mt-2 text-sm text-gray-600">{brand.clientAddress}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bag Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Bag Specifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Dimensions</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Bag Size:</strong> {brand.bagSizeWidth || brand.bagWidth || 0}" × {brand.bagSizeLength || brand.bagLength || 0}"</p>
                      <p><strong>Cutting Size:</strong> {brand.cuttingSizeWidth || 0}" × {brand.cuttingSizeLength || 0}"</p>
                      <p><strong>Capacity:</strong> {brand.capacity || 'Not specified'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Construction</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Top:</strong> {brand.top || brand.topMake || 'Not specified'}</p>
                      <p><strong>Bottom:</strong> {brand.bottom || 'Not specified'}</p>
                      <p><strong>Handle:</strong> {brand.handle || 'No'}</p>
                      <p><strong>Gusset:</strong> {brand.gusset || 'No'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Properties</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Bag Weight:</strong> {brand.bagWeight}g</p>
                      <p><strong>Perforation:</strong> {brand.perforation || 'No'}</p>
                      <p><strong>Lamination:</strong> {brand.lamination || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Material Specifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Palette className="w-5 h-5 mr-2" />
                  Material Specifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">PP Woven Fabric</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Fabric Variant:</strong> {brand.fabricVariant || 'Not specified'}</p>
                      <p><strong>Fabric Color:</strong> {brand.fabricColor || 'Not specified'}</p>
                      <p><strong>Fabric Grams (GSM):</strong> {brand.fabricGrams || brand.fabricGSM || 'Not specified'}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">BOPP Details</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Printing Material:</strong> {brand.printingMaterial || 'Not specified'}</p>
                      <p><strong>BOPP Film Size:</strong> {brand.boppFilmSize ? `${brand.boppFilmSize}mm` : 'Not specified'}</p>
                      <p><strong>BOPP Micron:</strong> {brand.boppMicron || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Metallic Properties</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Metallic:</strong> {brand.metallic || 'No'}</p>
                      {brand.metallic === 'Yes' && (
                        <>
                          <p><strong>Metallic Microns:</strong> {brand.metallicMicrons || 'Not specified'}</p>
                          <p><strong>Metallic Film Size:</strong> {brand.metallicFilmSize ? `${brand.metallicFilmSize}mm` : 'Not specified'}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Window Details</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Window:</strong> {brand.window || 'No'}</p>
                      {brand.window === 'Yes' && (
                        <p><strong>Window Film Size:</strong> {brand.windowMetallicFilmSize || 'Not specified'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cylinder Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Cylinder Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Printing Area</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Width:</strong> {brand.printingAreaWidth || 0}mm</p>
                      <p><strong>Circumference:</strong> {brand.printingAreaCircum || 0}mm</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Cylinder Size</h4>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p><strong>Width:</strong> {brand.cylinderSizeWidth || 0}mm</p>
                      <p><strong>Circumference:</strong> {brand.cylinderSizeCircum || 0}mm</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Cylinder Count</h4>
                    <p className="mt-2 text-sm text-gray-600">
                      <strong>{brand.numberOfCylinders || 0}</strong> cylinders
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">White Cylinder</h4>
                    <p className="mt-2 text-sm text-gray-600">{brand.whiteCylinder || 'No'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Common Cylinder</h4>
                    <p className="mt-2 text-sm text-gray-600">{brand.commonCylinder || 'Not specified'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Notes & Comments ({brand.userNotes ? brand.userNotes.length : 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {brand.userNotes && brand.userNotes.length > 0 ? (
                  <div className="space-y-4">
                    {brand.userNotes.map((note, index) => (
                      <div key={note.id || index} className="border-l-4 border-l-blue-500 bg-gray-50 p-4 rounded-r">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{note.userName}</span>
                            <span className="text-gray-400">({note.userRole})</span>
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span>{note.createdAt}</span>
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
                        {note.lastEdited && (
                          <p className="text-xs text-gray-500 mt-2 italic">
                            Last edited: {note.lastEdited}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No notes available for this brand specification.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Status & Key Info */}
            <Card>
              <CardHeader>
                <CardTitle>Status & Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Status</h4>
                  <Badge className={brand.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {brand.status}
                  </Badge>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Creation Details</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    {brand.createdDate && (
                      <p><strong>Created:</strong> {new Date(brand.createdDate).toLocaleDateString()}</p>
                    )}
                    {brand.createdBy && (
                      <p><strong>Created By:</strong> {brand.createdBy}</p>
                    )}
                    {brand.lastModified && (
                      <p><strong>Modified:</strong> {new Date(brand.lastModified).toLocaleDateString()}</p>
                    )}
                    {brand.modifiedBy && (
                      <p><strong>Modified By:</strong> {brand.modifiedBy}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Brand Artwork */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Brand Artwork
                </CardTitle>
              </CardHeader>
              <CardContent>
                {brand.artworkImageUrl ? (
                  <div>
                    {brand.artworkFileType === 'application/pdf' ? (
                      <div className="flex items-center p-4 border rounded bg-gray-50">
                        <svg className="w-8 h-8 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V6H8l-2-2z" clipRule="evenodd"/>
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{brand.artworkFileName || 'Brand Artwork.pdf'}</p>
                          <p className="text-xs text-gray-500">PDF Document</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <img 
                          src={brand.artworkImageUrl} 
                          alt={`${brand.brandName} artwork`}
                          className="w-full rounded-lg shadow-sm border"
                        />
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          {brand.artworkFileName || `${brand.brandName} Artwork`}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No artwork uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cylinders</span>
                  <span className="font-medium">{brand.numberOfCylinders || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Notes</span>
                  <span className="font-medium">{brand.userNotes ? brand.userNotes.length : 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Artwork</span>
                  <span className="font-medium">{brand.artworkImageUrl ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bag Weight</span>
                  <span className="font-medium">{brand.bagWeight}g</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBrandSpecs;